import {useEffect, useState, useMemo} from 'react'
import axios from 'axios'
import {useReduxAuth} from '../../../auth'
import {toast} from 'react-toastify'
import DataTableList from '../../components/DataTableList'
import {DOCTOR_HEADERS} from '../../../../constants/RoleConstants'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import CreateUserFrom from '../../components/forms/CreateUserForm'
import {filterHeadersByUserRole} from '../../helper/helperFunction'
import MetaTags from '../../../../meta/MetaTags'
import ConfirmationModal from '../../components/common/modals/ConfirmationModal'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import Loader from '../../components/Loader'
import {ActionDefaults} from '../../modules/accounts/components/CRUDWrapper/DefaultActions'
import apiClient from '../../../../services/api-client'

const DoctorListingPage = () => {
  // toast alerts
  const notifyError = (msg: string) => toast.error(msg)
  const notifySuccess = (msg: string) => toast.success(msg)
  const notifyWarn = (msg: string) => toast.warn(msg)

  const [userData, setUserData] = useState<any>([])
  const [loading, setLoading] = useState(true)
  const [isInviteLoading, setIsInviteLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [isStatusConfirmationModalOpen, setIsStatusConfirmationModalOpen] = useState(false)

  const [selectedDoctor, setSelectedDoctor] = useState<any>(null)
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false)
  const [isStatusLoading, setIsStatusLoading] = useState(false)
  const [loadingDoctorId, setLoadingDoctorId] = useState<number | null>(null)

  // Filter states
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('active')
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('all')
  const [manualSubscriptionFilter, setManualSubscriptionFilter] = useState<string>('all')

  // Overeride Tail
  const [isOverrideTrialModalOpen, setIsOverrideTrialModalOpen] = useState(false)
  const [trialLimit, setTrialLimit] = useState<string>('5')

  const {currentUser} = useReduxAuth()

  // function to get data
  const fetchData = async () => {
    setLoading(true)
    let response

    try {
      response = await axios.get(API_ENDPOINTS.GET_ALL_DOCTORS())

      const items = response?.data?.data || []
      setUserData(items)
    } catch (error: any) {
      notifyError(error?.response?.data?.message)
    } finally {
      setLoading(false)
    }
  }

  // fetch data
  useEffect(() => {
    fetchData()
  }, [])

  // create new user(nurse)
  const handleSubmit = async (values: any, resetForm: () => void) => {
    setIsInviteLoading(true)
    let response
    try {
      // create mode
      response = await axios.post(API_ENDPOINTS.INVITE_USER('doctor'), values)
      notifySuccess(response?.data?.message)

      // Fetch updated data and close the modal
      await fetchData()
    } catch (error: any) {
      notifyError(error?.response?.data?.message)
    } finally {
      setIsInviteLoading(false)
      setIsModalOpen(false)
      resetForm()
    }
  }

  // handle update status
  const handleUpdateStatus = async (row: any) => {
    // Prevent multiple clicks
    if (isStatusLoading) return

    setIsStatusLoading(true)
    setLoadingDoctorId(row.id)
    
    // toggle status
    const status = row.status === 1 ? 0 : 1

    try {
      const response = await axios.put(API_ENDPOINTS.UPDATE_USER_STATUS(row?.id, status))

      notifySuccess(response?.data?.message)

      // Create a new updated user data array
      const updatedUserData = userData.map((user: any) =>
        user.id === row.id ? {...user, status} : user
      )

      // Update state with the new user data
      setUserData(updatedUserData)
    } catch (error: any) {
      notifyError(error?.response?.data?.message)
    }
    finally{
      setIsStatusLoading(false)
      setLoadingDoctorId(null)
      setIsStatusConfirmationModalOpen(false)
    }
  }
  const openConfirmationModal = (row : any) => {
    setSelectedDoctor(row);
    setIsStatusConfirmationModalOpen(true);
  }
  // handle trial subscription
  const handleDoctorTrialSubscription = async (row: any) => {
    if (
      row?.subscription_status !== 'inactive' &&
      row?.subscription_status !== 'terminated' &&
      row?.subscription_status !== 'trial'
    ) {
      notifyWarn('Trial can only be activated for inactive or terminated subscriptions')
      return
    }
    setSelectedDoctor(row)
    setIsConfirmationModalOpen(true)
  }

  const handleConfirmSubscription = async () => {
    if (!selectedDoctor) return

    setIsSubscriptionLoading(true)
    try {
      const response = await axios.put(
        API_ENDPOINTS.TRIAL_SUBSCRIPTION_DOCTOR(
          selectedDoctor?.id,
          selectedDoctor?.subscription_status !== 'trial'
        )
      )
      notifySuccess(response?.data?.message)

      // Update the user data with new subscription status
      const updatedUserData = userData.map((user: any) =>
        user.id === selectedDoctor.id
          ? {
              ...user,
              subscription_status:
                selectedDoctor.subscription_status !== 'trial' ? 'trial' : 'inactive',
            }
          : user
      )
      setUserData(updatedUserData)
    } catch (error: any) {
      notifyError(error?.response?.data?.message)
    } finally {
      setIsSubscriptionLoading(false)
      setIsConfirmationModalOpen(false)
      setSelectedDoctor(null)
    }
  }

  // Apply filters to data
  const filteredData = useMemo(() => {
    return userData.filter((doctor: any) => {
      // Search text filter
      const searchFilter =
        searchText === '' ||
        doctor.full_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        doctor.email?.toLowerCase().includes(searchText.toLowerCase())

      // Status filter
      const statusFilterMatch =
        statusFilter === 'all' ||
        (statusFilter === 'active' && doctor.status === 1) ||
        (statusFilter === 'inactive' && doctor.status === 0)

      // Payment filter - matching subscription_status of 'active' to 'Paid' display
      const subscriptionFilterMatch =
        subscriptionFilter === 'all' ||
        (subscriptionFilter === 'trial' && doctor.subscription_status === 'trial') ||
        (subscriptionFilter === 'paid' && doctor.subscription_status === 'active') ||
        (subscriptionFilter === 'inactive' && doctor.subscription_status === 'inactive')||
        (subscriptionFilter === 'incomplete' && doctor.subscription_status === 'incomplete')||
        (subscriptionFilter === 'past_due' && doctor.subscription_status === 'past_due')||
        (subscriptionFilter === 'cancelled' && doctor.subscription_status === 'cancelled')||
        (subscriptionFilter === 'terminated' && doctor.subscription_status === 'terminated')

      // Manual subscription filter
      const manualSubFilterMatch =
        manualSubscriptionFilter === 'all' ||
        (manualSubscriptionFilter === 'yes' && doctor.subscription_status === 'trial') ||
        (manualSubscriptionFilter === 'no' && doctor.subscription_status !== 'trial')

      return searchFilter && statusFilterMatch && subscriptionFilterMatch && manualSubFilterMatch
    })
  }, [userData, searchText, statusFilter, subscriptionFilter, manualSubscriptionFilter])

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    switch (key) {
      case 'search':
        setSearchText(value)
        break
      case 'status':
        setStatusFilter(value)
        break
      case 'subscription':
        setSubscriptionFilter(value)
        break
      case 'manualSubscription':
        setManualSubscriptionFilter(value)
        break
      default:
        break
    }
  }

  // Clear all filters
  const handleClearAllFilters = () => {
    setSearchText('')
    setStatusFilter('all')
    setSubscriptionFilter('all')
    setManualSubscriptionFilter('all')
  }

  // Function to filter headers based on user permissions
  const filteredHeaders = filterHeadersByUserRole(DOCTOR_HEADERS, currentUser)

  // Define custom filters for DataTableList
  const customFilters = [
    {
      name: 'Search',
      key: 'search',
      type: 'search' as const,
      placeholder: 'Search by name or email',
      value: searchText,
    },
    {
      name: 'Status',
      key: 'status',
      type: 'select' as const,
      value: statusFilter,
      options: [
        {value: 'all', label: 'All'},
        {value: 'active', label: 'Active'},
        {value: 'inactive', label: 'Inactive'},
      ],
    },
    {
      name: 'Subscription',
      key: 'subscription',
      type: 'select' as const,
      value: subscriptionFilter,
      options: [
        {value: 'all', label: 'All'},
        {value: 'trial', label: 'Trial'},
        {value: 'paid', label: 'Active'}, // TODO:: change to Active, it's not one time paid to be called that.
        {value: 'inactive', label: 'Inactive'},
        {value: 'incomplete', label: 'Incomplete'},
        {value: 'past_due', label: 'Past due'}, // TODO:: change to Past Due, it's not one time paid to be called that.
        {value: 'cancelled', label: 'Cancelled'},
        {value: 'terminated', label: 'Terminated'},

        // TODO:: Fix these and remove separate filter for trial
        // {value: 'trial', label: 'Trial'},
        // {value: 'terminated', label: 'Terminated'},
        // {value: 'cancelled', label: 'Cancelled'},
        // TODO:: add trial subscription
      ],
    },
    {
      name: 'Trial',
      key: 'manualSubscription',
      type: 'select' as const,
      value: manualSubscriptionFilter,
      options: [
        {value: 'all', label: 'All'},
        {value: 'yes', label: 'Active'},
        {value: 'no', label: 'Inactive'},
      ],
      displayValueCallback: (value: string) => (value === 'yes' ? 'Active' : 'Inactive'),
    },
  ]

  // Define actions for doctors
  const getActionsForDoctor = (row: any) => [
  ActionDefaults.activate,
  ActionDefaults.deactivate,
  ActionDefaults.resendEmailToDoctor,
  ...(row.subscription_status === 'trial' ? [ActionDefaults.setTrialLimit] : []),
];

  const handleResendDoctorInvite = async (row: any) => {
    if (!row) return;

    try {
      const response = await apiClient.put(API_ENDPOINTS.RESEND_INVITE_DOCTOR(row.id));
      notifySuccess(response?.data?.message || 'Invite resent successfully');
    } catch (error: any) {
      notifyError(error?.response?.data?.message || 'Failed to resend invite');
    }
  };

  // Handle action click
  const handleAction = (action: any, row: any) => {
    // Prevent actions while loading  
    if (isStatusLoading) return

    switch (action.name) {
      case 'ACTIVATE':
        if (row.status === 0) {
          openConfirmationModal(row)
        }
        break
      case 'DEACTIVATE':
        if (row.status === 1) {
          openConfirmationModal(row)
        }
        break
      case 'SET_TRIAL':
        setSelectedDoctor(row)
        setTrialLimit(String(row.trial_limit ?? '')) 
        setIsOverrideTrialModalOpen(true)  
        break
      case 'RESEND_EMAIL_TO_DOCTOR':
        handleResendDoctorInvite(row);
        break;  
      default:
        console.log('Unknown action:', action.name)
    }
  }

  return (
    <>
      <MetaTags title={'Doctor List'} />
      {isInviteLoading ? (
        <Loader />
      ) : (
        <>
          <DataTableList
            title='Doctor'
            columns={filteredHeaders}
            data={filteredData}
            getRowActions={getActionsForDoctor}
            onAction={handleAction}
            loadingRowId={loadingDoctorId}
            showAddButton={
              currentUser?.role_id === USER_ROLES.ADMIN ||
              currentUser?.role_id === USER_ROLES.SUB_ADMIN
            }
            onAddClick={() => setIsModalOpen(!isModalOpen)}
            addButtonText='Add Doctor'
            isLoading={loading}
            handleStatusToggle={openConfirmationModal}
            handleManualSubscription={handleDoctorTrialSubscription}
            requiredPermissionToUpdateStatus='doctor-status:update'
            showCustomFilters={true}
            customFilters={customFilters}
            onCustomFilterChange={handleFilterChange}
            onClearAllCustomFilters={handleClearAllFilters}
          />
        </>
      )}

      {/* create user form */}
      <CreateUserFrom
        title='Invite Doctor'
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isLoading={isInviteLoading}
        handleSubmit={handleSubmit}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => {
          setIsConfirmationModalOpen(false)
          setSelectedDoctor(null)
        }}
        onConfirm={handleConfirmSubscription}
        title={
          selectedDoctor?.subscription_status === 'trial'
            ? 'Deactivate Trial Subscription'
            : 'Activate Trial Subscription'
        }
        message={`Are you sure you want to ${
          selectedDoctor?.subscription_status === 'trial' ? 'deactivate' : 'activate'
        } trial subscription for ${selectedDoctor?.full_name}?`}
        confirmButtonText={
          selectedDoctor?.subscription_status === 'trial' ? 'Deactivate Trial' : 'Activate Trial'
        }
        isLoading={isSubscriptionLoading}
      />

      <ConfirmationModal
        isOpen={isStatusConfirmationModalOpen}
        onClose={() => {
          if (!isStatusLoading) {
            setIsStatusConfirmationModalOpen(false)
            setSelectedDoctor(null)
          }
        }}
        onConfirm={()=>handleUpdateStatus(selectedDoctor)}
        title={
          selectedDoctor?.status === 1
            ? 'Deactivate Account'
            : 'Activate Account'
        }
        message={`Are you sure you want to ${
          selectedDoctor?.status === 1 ? 'deactivate' : 'activate'
        } account status for ${selectedDoctor?.full_name}?`}
        confirmButtonText={
          selectedDoctor?.status === 1 ? 'Deactivate' : 'Activate'
        }
        isLoading={isStatusLoading}
      />

      <ConfirmationModal
        isOpen={isOverrideTrialModalOpen}
        onClose={() => {
          setIsOverrideTrialModalOpen(false)
          setSelectedDoctor(null)
          setTrialLimit('')
        }}
        onConfirm={async () => {
          if (!selectedDoctor || !trialLimit) return

          setIsSubscriptionLoading(true)
          try {
            const response = await axios.post(
              API_ENDPOINTS.ADMIN_OVERRIDE_DOCTOR_TRIAL(selectedDoctor.id),
              { trial_limit: Number(trialLimit) },

            )
            notifySuccess(response?.data?.message || 'Trial limit updated successfully')
            setIsOverrideTrialModalOpen(false)
            setTrialLimit('')
            fetchData()
          } catch (error: any) {
            notifyError(error?.response?.data?.message || 'Failed to update trial limit')
          } finally {
            setIsSubscriptionLoading(false)
            setSelectedDoctor(null)
          }
        }}
        title="Override Trial Limit"
        customContent={
          <>
            <div>Enter new trial patient limit for <b>{selectedDoctor?.full_name}</b>:</div>
            <input
              type="number"
              className="form-control mt-2"
              min={1}
              value={trialLimit}
              onChange={(e) => setTrialLimit(e.target.value)}
            />
          </>
        }
        confirmButtonText="Update Limit"
        isLoading={isSubscriptionLoading}
      />
    </>
  )
}

export default DoctorListingPage
